import { popProp, validStatus } from "@/utils/helpers";
import api from "../config/api";
import { camelToSnakeCase } from "@/utils/string-manipulation";

export const fetchInvoices = async ({
  id = "",
  type = "client",
  filters,
}: any) => {
  const clientCognitoId = JSON.parse(localStorage.getItem("PLATFORM_ID") || "");
  if (!clientCognitoId) {
    throw new Error("It was not possible to identify the platform.");
  }

  const isCustomer = type === "customer";

  const url = `/platform/invoices/${
    isCustomer
      ? `customer/${clientCognitoId}|${id || clientCognitoId}`
      : id || clientCognitoId
  }${filters?.contactId ? `/contact/${filters?.contactId}` : ""}`;

  const { data } = await api.get(encodeURI(url));
  return data;
};

export const fetchPayableInvoices = async ({ userId }: any) => {
  if (!userId) {
    throw new Error("[FETCH_PAYABLE_INVOICES] Contact ID missing.");
  }

  const url = `/platform/invoices/${userId}/ap`;

  const { data } = await api.get(encodeURI(url));
  return data;
};

export const fetchContactInvoices = async ({ userId, contactId }: any) => {
  if (!userId) {
    throw new Error("[FETCH_CONTACT_INVOICES] User ID missing.");
  }

  if (!contactId) {
    throw new Error("[FETCH_CONTACT_INVOICES] Contact ID missing.");
  }

  const url = `/platform/invoices/${userId}/contact/${contactId}`;
  const { data } = await api.get(encodeURI(url));

  return data;
};

export const fetchInvoicesKPIs = async ({ userId, params }: any) => {
  if (!userId) {
    throw new Error("[FETCH_INVOICES_KPIS] User ID missing.");
  }

  let queryArr: string[] = [];
  for (const [key, value] of Object.entries(params)) {
    let k = camelToSnakeCase(key);
    queryArr.push(`${k}=${value}`);
  }

  let query = queryArr.join("&");
  const { data } = await api.get(
    encodeURI(
      `/platform/invoices/${userId}/kpis?${
        query.includes("days_back") ? "" : "days_back=30"
      }&${query}`
    )
  );

  return data;
};

export const getInvoiceById = async ({ userId, invoiceId }: any) => {
  if (!userId) {
    throw new Error("[GET_INVOICE_BY_ID] User ID missing.");
  }

  if (!invoiceId) {
    throw new Error("[GET_INVOICE_BY_ID] Invoice ID missing.");
  }

  const { data } = await api.get(`/platform/invoices/${userId}/${invoiceId}`);

  return data;
};

export const getInvoicesByContact = async ({ userId, contactId }: any) => {
  if (!userId) {
    throw new Error("[GET_INVOICES_BY_CONTACT] User ID missing.");
  }

  if (!contactId) {
    throw new Error("[GET_INVOICES_BY_CONTACT] Contact ID missing.");
  }

  const { data } = await api.get(
    `/platform/invoices/${userId}/contact/${contactId}`
  );

  return data;
};

export const createInvoice = async (invoiceData: any) => {
  try {
    let accessToken = popProp(invoiceData, "accessToken");
    let userId = popProp(invoiceData, "userId");
    const config = {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
        "x-api-key": import.meta.env.VITE_PBTECH_API_KEY,
      },
      validateStatus: validStatus,
    };

    const response = await api.post(
      `/platform/invoices/${userId}`,
      invoiceData,
      config
    );

    // You can handle specific status codes here after the request has been made
    if (response.status !== 201) {
      throw new Error(
        "The request was made and the server responded, but status code is not 201"
      );
    }

    return response;
  } catch (error: any) {
    console.log("Error creating invoice: ", error);
    let error_message;
    if (error.response) {
      // The request was made and the server responded with a status code that falls out of the range of 2xx
      switch (error.response.status) {
        case 401:
          error_message =
            "Your session has expired. Please log out and log in again.";
          break;
        case 403:
          error_message = "You do not have permission to create an invoice.";
          break;
        case 409:
          error_message = error.detail;
          break;
        case 422:
          if (error.detail && error.detail.length > 0) {
            error.detail.map((item: any) => {
              if (item.loc[1] === "invoice_due_date")
                error_message = "Due date is required";
              if (item.loc[1] === "invoice_send_date")
                error_message = "Send date is required";
              if (
                item.loc[1] === "invoice_line_items" &&
                item.loc[3] === "description"
              )
                error_message = "Line items description is required";
              if (
                item.loc[1] === "invoice_line_items" &&
                item.loc[3] === "quantity"
              )
                error_message = "Line items quantity is required";
              if (
                item.loc[1] === "invoice_line_items" &&
                item.loc[3] === "unit_price"
              )
                error_message = "Line items price is required";
              if (item.loc[1] === "invoice_tax_percentage")
                error_message = "Tax is required";
            });
          }
          break;
        default:
          error_message = `Error creating invoice: ${error}`;
          break;
      }
      throw new Error(error_message);
    }

    // if (error.request) { // The request was made but no response was received
    //     // This could be a network error, or some other reason why the request was not fulfilled
    //     error_message = "The request was made but no response was received.";
    //     throw new Error(error_message);
    // }

    // Something happened in setting up the request that triggered an Error
    // throw error;
  }
};

export const deleteInvoice = async ({ userId, invoiceId }: any) => {
  if (!userId) {
    throw new Error("[DELETE_INVOICE] User ID missing.");
  }

  if (!invoiceId) {
    throw new Error("[DELETE_INVOICE] Invoice ID missing.");
  }

  const { data } = await api.delete(
    `/platform/invoices/${userId}/${invoiceId}`
  );

  return data;
};

export const updateInvoice = async ({ userId, invoiceId, payload }: any) => {
  if (!userId) {
    throw new Error("[UPDATE_INVOICE] User ID missing.");
  }

  if (!invoiceId) {
    throw new Error("[UPDATE_INVOICE] Invoice ID missing.");
  }

  const { data } = await api.patch(
    `/platform/invoices/${userId}/${invoiceId}`,
    payload
  );

  return data;
};

export const payInvoice = async ({ userId, payload }: any) => {
  if (!userId) {
    throw new Error("[PAY_INVOICE] User ID missing.");
  }

  const { data } = await api.post(
    `/platform/invoices/${userId}/pay_invoice`,
    payload
  );

  return data;
};

export const uploadInvoicePDF = async ({ userId, params, pdfBlob }: any) => {
  if (!userId) {
    throw new Error("[UPLOAD_INVOICE_PDF] User ID missing.");
  }

  try {
    // Get URL
    const { data: url } = await api.get(
      `/settings/upload_url/${userId}?${params}`
    );
    // Upload New way
    const presignedData = url.uploadURLData;
    const formData = new FormData();
    Object.keys(presignedData.fields).forEach((key) => {
      formData.append(key, presignedData.fields[key]);
    });
    formData.append("file", pdfBlob, "invoice.pdf");
    const uploadResponse = await fetch(presignedData.url, {
      method: "POST",
      body: formData,
      redirect: "follow",
    });
    return { url: url, response: uploadResponse };
  } catch (err) {
    console.error(err);
  }
};

export const sendInvoiceToCustomer = async ({
  userId,
  invoiceId,
  reminder = false,
}: any) => {
  if (!userId) {
    throw new Error("[SEND_INVOICE_TO_CUSTOMER] User ID missing.");
  }
  if (!invoiceId) {
    throw new Error("[SEND_INVOICE_TO_CUSTOMER] Invoice ID missing.");
  }

  const { data } = await api.post(
    `/platform/invoices/${userId}/send/${invoiceId}?reminder=${reminder}`,
    {}
  );

  return data;
};

export const bulkSendInvoices = async (userId, invoices) => {
  if (!userId) {
    throw new Error("[BULK_SEND] User ID missing.");
  }
  if (!invoices) {
    throw new Error("[BULK_SEND] Invoice list missing.");
  }

  const { data } = await api.post(
    `/platform/invoices/${userId}/send/bulk`,
    invoices
  );

  return data;
};

export const downloadInvoice = async ({ userId, payload }: any) => {
  if (!userId) {
    throw new Error("[DOWNLOAD_INVOICE] User ID missing.");
  }

  const { data } = await api.post(`/settings/download_url/${userId}`, payload);

  return data;
};

export const processInvoiceImage = async (file: any) => {
  const toBase64 = (file: any) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });

  let base64Image: any;
  try {
    const base64Content: any = await toBase64(file);
    base64Image = base64Content.split(",")[1];
  } catch (error) {
    console.log("INVOICE_IMAGE::FILE_READ::ERROR::", error);
    throw new Error("Error reading file content");
  }

  const fileType = file.type === "application/pdf" ? "pdf" : "image";
  const payload = {
    file_type: fileType,
    content: base64Image,
  };

  const { data } = await api.post(
    `/platform/invoices/process_invoice_image`,
    payload
  );
  return data;
};

export const getTemplateUrl = async () => {
  const { data } = await api.get(`/platform/invoices/template/download`);
  return data;
};
